<script lang="ts">
  import type {
    BridgeVesselPhase,
    PhysicalBridgeState,
    PublicSnapshot,
    PublicVesselGroup,
    PublicVesselSummary,
  } from "../../shared/contracts";
  import {
    formatDistanceToBrickell,
    formatEtTime,
    numeric,
    vesselDisplayName,
    vesselKindLabel,
  } from "../format";
  import {
    likelyTowRelationship,
    trafficMovements,
    type LikelyTowMovement,
  } from "../tow-groups";
  import { CircleHelp } from "lucide-svelte";
  import { vesselFacesBrickell } from "../vessel-heading";
  import UnknownVesselBadge from "./UnknownVesselBadge.svelte";

  export let vessels: PublicVesselSummary[];
  export let vesselGroups: PublicVesselGroup[] = [];
  export let bridgeState: PhysicalBridgeState;
  export let selectedVesselId: string | null = null;
  export let dataMode: PublicSnapshot["dataMode"] = "live";
  export let legendOpen = false;
  export let onlegendopen: (trigger: HTMLElement) => void = () => {};
  export let onvesselselect: (
    vessel: PublicVesselSummary,
    trigger: HTMLElement,
  ) => void;

  const AT_BRIDGE_METERS = 60;

  const bridgeStateLabel: Record<PhysicalBridgeState, string> = {
    up: "OPEN",
    down: "CLOSED",
    unknown: "UNKNOWN",
  };

  const phaseLabel: Record<BridgeVesselPhase, string> = {
    approaching: "Approaching Brickell",
    waiting: "Waiting near Brickell",
    clearing: "Clearing Brickell",
  };

  let failedAssets = new Set<string>();

  $: bridgeLabel = bridgeStateLabel[bridgeState];
  $: trafficModeLabel = dataMode === "demo" ? "DEMO DATA" : "LIVE";
  $: movements = trafficMovements(vessels, vesselGroups);

  function markAssetFailed(id: string): void {
    failedAssets = new Set(failedAssets).add(id);
  }

  function movementLabel(vessel: PublicVesselSummary): string {
    if (vessel.bridgePhase === "waiting") return `${phaseLabel.waiting} · Holding`;
    const movingUpriver = vessel.bridgePhase === "approaching"
      ? vessel.sMeters < 0
      : vessel.sMeters > 0;
    return `${phaseLabel[vessel.bridgePhase]} · ${movingUpriver ? "Upriver" : "Downriver"}`;
  }

  function timingLabel(vessel: PublicVesselSummary): string {
    if (Math.abs(vessel.sMeters) <= AT_BRIDGE_METERS) return "AT BRIDGE";
    if (vessel.bridgePhase === "clearing") return "PASSING";
    if (vessel.bridgePhase === "waiting") return "WAITING";
    if (vessel.etaMinutes === null) return "NO ETA";
    const minutes = Math.max(0, Math.round(vessel.etaMinutes));
    return minutes < 1 ? "<1 MIN" : `${minutes} MIN`;
  }

  function speedLabel(vessel: PublicVesselSummary): string {
    return numeric(vessel.speedKnots, "kn", 1) ?? "Speed unavailable";
  }

  function distanceLabel(vessel: PublicVesselSummary): string {
    return formatDistanceToBrickell(vessel.distanceToBrickellMeters) ?? "Distance unavailable";
  }

  function rowAriaLabel(vessel: PublicVesselSummary, relationship?: string): string {
    const name = vesselDisplayName(vessel);
    return `View ${name}: ${relationship ? `${relationship}, ` : ""}${vessel.knownOpener ? "known Brickell opener, " : ""}${movementLabel(vessel)}, ${timingLabel(vessel)}, ${speedLabel(vessel)}, ${distanceLabel(vessel)}, updated ${formatEtTime(vessel.observedAt)}`;
  }

  function groupMembers(movement: LikelyTowMovement): PublicVesselSummary[] {
    return [...movement.tugs, ...movement.tows];
  }

  function groupRole(vessel: PublicVesselSummary, movement: LikelyTowMovement): string {
    const tugIndex = movement.tugs.findIndex((candidate) => candidate.id === vessel.id);
    const reportedType = vesselKindLabel(vessel.type).toUpperCase();
    if (tugIndex >= 0) return vessel.type === "tug" ? "TUG" : `${reportedType} · TOWING VESSEL`;
    return `${reportedType} · TOW`;
  }

  function groupAriaLabel(movement: LikelyTowMovement, ordinal: number): string {
    const tugNames = movement.tugs.map(vesselDisplayName).join(" and ");
    const towNames = movement.tows.map(vesselDisplayName).join(" and ");
    return `Movement ${ordinal}, towing unit: ${tugNames} towing ${towNames}`;
  }
</script>

<aside
  class:live-traffic--empty={vessels.length === 0}
  class="live-traffic"
  data-mode={dataMode}
  aria-labelledby="live-traffic-label live-traffic-location"
>
  <header class="live-traffic__header">
    <div class="live-traffic__heading">
      <p id="live-traffic-label" class="live-traffic__kicker">
        LIVE TRAFFIC <span aria-hidden="true">·</span> {vessels.length} {vessels.length === 1 ? "VESSEL" : "VESSELS"}
      </p>
      <button
        class="live-traffic__legend-button"
        type="button"
        aria-label="Vessel types"
        aria-expanded={legendOpen}
        onclick={(event) => onlegendopen(event.currentTarget)}
      >
        <CircleHelp size={14} strokeWidth={2.1} aria-hidden="true" />
        <span>Vessel types</span>
      </button>
    </div>
    <div class="live-traffic__location-row">
      <h2 id="live-traffic-location">BRICKELL</h2>
      <div class="live-traffic__location-meta">
        <span class="live-traffic__mode">
          {movements.length} {movements.length === 1 ? "MOVEMENT" : "MOVEMENTS"}<span class="live-traffic__mode-state"> · {trafficModeLabel}</span>
        </span>
        <span
          class:live-traffic__bridge-chip--up={bridgeState === "up"}
          class:live-traffic__bridge-chip--down={bridgeState === "down"}
          class:live-traffic__bridge-chip--unknown={bridgeState === "unknown"}
          class="live-traffic__bridge-chip"
          aria-label={`Brickell bridge ${bridgeLabel.toLowerCase()}`}
        >{bridgeLabel}</span>
      </div>
    </div>
  </header>

  {#if vessels.length > 0}
    <ol class="live-traffic__list" aria-label="Vessel movements relevant to Brickell">
      {#each movements as trafficMovement, index (trafficMovement.id)}
        {#if trafficMovement.kind === "likely_tow"}
          {@const lead = trafficMovement.tugs[0]}
          {@const groupSelected = trafficMovement.vessels.some((vessel) => vessel.id === selectedVesselId)}
          <li
            class:live-traffic__item--selected={groupSelected}
            class="live-traffic__item live-traffic__item--tow"
            data-vessel-group-id={trafficMovement.group.id}
          >
            <div
              class="tow-unit"
              role="group"
              aria-label={groupAriaLabel(trafficMovement, index + 1)}
            >
              <span class="live-traffic__number tow-unit__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div class="tow-unit__heading">
                <strong>TOWING UNIT</strong>
                <span>{timingLabel(lead)}</span>
              </div>
              <div class="tow-unit__members" role="group" aria-label="Towing unit vessels">
                {#each groupMembers(trafficMovement) as vessel (vessel.id)}
                  {@const selected = vessel.id === selectedVesselId}
                  <button
                    class:tow-unit__member--selected={selected}
                    class="tow-unit__member"
                    type="button"
                    aria-label={rowAriaLabel(vessel, likelyTowRelationship(vessel, trafficMovement))}
                    aria-pressed={selected}
                    onclick={(event) => onvesselselect(vessel, event.currentTarget)}
                  >
                    <span class="tow-unit__art" aria-hidden="true">
                      {#if !failedAssets.has(vessel.id)}
                        <img
                          class:mirrored={vesselFacesBrickell(vessel)}
                          src={`/assets/vessels/${vessel.type}-compact.webp`}
                          alt=""
                          decoding="async"
                          onerror={() => markAssetFailed(vessel.id)}
                        />
                      {:else}
                        <svg
                          class:mirrored={vesselFacesBrickell(vessel)}
                          viewBox="0 0 120 58"
                          focusable="false"
                        >
                          <path class="vessel-fallback__hull" d="M5 35H98L116 27L104 48H21Z" />
                          <path class="vessel-fallback__cabin" d="M35 34V15H76L91 34Z" />
                          <path class="vessel-fallback__window" d="M43 20H55V29H43ZM60 20H73L81 29H60Z" />
                          <path class="vessel-fallback__mast" d="M60 15V3M60 5L82 13" />
                        </svg>
                      {/if}
                      {#if vessel.type === "generic"}<UnknownVesselBadge />{/if}
                    </span>
                    <span class="tow-unit__identity">
                      <strong>{vesselDisplayName(vessel)}</strong>
                      <span>
                        {groupRole(vessel, trafficMovement)}
                        {#if vessel.knownOpener}
                          <span class="tow-unit__opener">KNOWN OPENER</span>
                        {/if}
                      </span>
                    </span>
                    <span class="tow-unit__member-meta">
                      <strong>{speedLabel(vessel)}</strong>
                      <time datetime={vessel.observedAt}>{formatEtTime(vessel.observedAt)}</time>
                    </span>
                  </button>
                {/each}
              </div>
              <div class="tow-unit__footer">
                <span>{movementLabel(lead)}</span>
                <span>{distanceLabel(lead)}</span>
              </div>
            </div>
          </li>
        {:else}
          {@const vessel = trafficMovement.vessels[0]}
          {@const name = vesselDisplayName(vessel)}
          {@const timing = timingLabel(vessel)}
          {@const movement = movementLabel(vessel)}
          {@const speed = speedLabel(vessel)}
          {@const distance = distanceLabel(vessel)}
          {@const selected = vessel.id === selectedVesselId}
          <li
            class:live-traffic__item--selected={selected}
            class="live-traffic__item"
            data-vessel-id={vessel.id}
          >
            <button
              class="live-traffic__vessel-button"
              type="button"
              aria-label={rowAriaLabel(vessel)}
              aria-pressed={selected}
              onclick={(event) => onvesselselect(vessel, event.currentTarget)}
            >
              <span class="live-traffic__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>

              <span class="live-traffic__vessel-art" aria-hidden="true">
                {#if !failedAssets.has(vessel.id)}
                  <img
                    class:mirrored={vesselFacesBrickell(vessel)}
                    src={`/assets/vessels/${vessel.type}-compact.webp`}
                    alt=""
                    decoding="async"
                    onerror={() => markAssetFailed(vessel.id)}
                  />
                {:else}
                  <svg
                    class:mirrored={vesselFacesBrickell(vessel)}
                    viewBox="0 0 120 58"
                    focusable="false"
                  >
                    <path class="vessel-fallback__hull" d="M5 35H98L116 27L104 48H21Z" />
                    <path class="vessel-fallback__cabin" d="M35 34V15H76L91 34Z" />
                    <path class="vessel-fallback__window" d="M43 20H55V29H43ZM60 20H73L81 29H60Z" />
                    <path class="vessel-fallback__mast" d="M60 15V3M60 5L82 13" />
                  </svg>
                {/if}
                {#if vessel.type === "generic"}<UnknownVesselBadge />{/if}
              </span>

              <span class="live-traffic__identity">
                <strong class="live-traffic__name">{name}</strong>
                <span class="live-traffic__type">{vesselKindLabel(vessel.type)}</span>
                <span class="live-traffic__movement">{movement}</span>
                {#if vessel.knownOpener}
                  <span class="live-traffic__opener">KNOWN OPENER</span>
                {/if}
              </span>

              <span class="live-traffic__timing">
                <strong>{timing}</strong>
                <span>{distance}</span>
              </span>

              <span class="live-traffic__meta">
                <span>{speed}</span>
                <time datetime={vessel.observedAt}>Updated {formatEtTime(vessel.observedAt)}</time>
              </span>
            </button>
          </li>
        {/if}
      {/each}
    </ol>
  {:else}
    <div class="live-traffic__empty" role="status">
      <strong>NO VESSELS BROADCASTING POSITION</strong>
      <p>
        {dataMode === "demo"
          ? "No sample position currently places a vessel on the Brickell opening route."
          : "No recent position broadcast currently places a vessel on the Brickell opening route."}
      </p>
    </div>
  {/if}
</aside>
